import { useState } from "react";

import { postFeedback } from "../../api";

type Label = "ok" | "ng";

/**
 * OK / NG operator feedback for a single assistant response.
 *
 * Posts to `/feedback` which records a numeric Langfuse score (`operator_label`:
 * OK=1 / NG=0) on this response's trace. Clicking the active label again clears
 * it (toggle). Only rendered when `feedback_enabled` (Langfuse on) and the
 * message carries a `trace_id`.
 */
export const FeedbackButtons = ({ traceId }: { traceId: string }) => {
    const [selected, setSelected] = useState<Label | null>(null);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(false);

    const send = async (label: Label) => {
        const next: Label | null = selected === label ? null : label; // toggle
        setSending(true);
        setError(false);
        try {
            // Only POST when setting a label; toggling off just updates local UI
            // (the last submitted score remains in Langfuse until re-labeled).
            if (next) {
                const res = await postFeedback(traceId, next);
                if (!res.ok) throw new Error(`feedback failed: ${res.status}`);
            }
            setSelected(next);
        } catch {
            setError(true);
        } finally {
            setSending(false);
        }
    };

    const btn = (label: Label, text: string, activeColor: string) => {
        const active = selected === label;
        return (
            <button
                type="button"
                onClick={() => send(label)}
                disabled={sending}
                aria-pressed={active}
                title={label === "ok" ? "Good answer" : "Bad answer"}
                style={{
                    cursor: sending ? "default" : "pointer",
                    border: `1px solid ${active ? activeColor : "#d0d0d0"}`,
                    background: active ? activeColor : "transparent",
                    color: active ? "#ffffff" : "#555555",
                    borderRadius: 6,
                    padding: "2px 10px",
                    fontSize: 12,
                    lineHeight: 1.6,
                }}
            >
                {text}
            </button>
        );
    };

    return (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
            {btn("ok", "👍 OK", "#107c10")}
            {btn("ng", "👎 NG", "#a4262c")}
            {error && <span style={{ color: "#a4262c", fontSize: 12 }}>Failed — retry</span>}
        </div>
    );
};

export default FeedbackButtons;
